import { useEffect, useState } from 'react';
import { getBlob } from '../api/client';
import './OrgAvatar.css';

interface OrgAvatarProps {
  orgId: number | null | undefined;
  /** Hint from the directory/org payload — skip the fetch when known false. */
  hasLogo?: boolean;
  /** Company name, used for alt text. */
  name?: string | null;
  size?: number;
  /** Bump to force a re-fetch when the logo bytes change but `hasLogo` doesn't
   *  (e.g. replacing an existing logo, where it stays `true`). */
  refreshKey?: number | string;
}

/** Rounded-square company logo tile (distinct from the round <PersonAvatar>).
 *  Fetches GET /api/organizations/:id/logo (auth header) and renders an object
 *  URL; falls back to a building glyph when there's no logo. */
export function OrgAvatar({ orgId, hasLogo, name, size = 36, refreshKey }: OrgAvatarProps): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    setUrl(null);
    if (!orgId || hasLogo === false) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    void (async () => {
      try {
        const blob = await getBlob(`/api/organizations/${orgId}/logo`);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch {
        /* 404 / no logo → building-glyph fallback */
      }
    })();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [orgId, hasLogo, refreshKey]);

  const dim = { width: size, height: size };
  if (url) {
    return <img src={url} alt={name ?? 'company'} className="org-avatar" style={dim} />;
  }
  return (
    <span className="org-avatar org-avatar-fallback" style={dim} aria-label={name ?? 'company'} role="img">
      <svg viewBox="0 0 24 24" width={Math.round(size * 0.56)} height={Math.round(size * 0.56)}
           fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"
           aria-hidden="true">
        <path d="M3 21h18" />
        <path d="M6 21V5a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v16" />
        <path d="M15 21V9h3a1 1 0 0 1 1 1v11" />
        <path d="M9 7h.01M9 10h.01M9 13h.01M12 7h.01M12 10h.01M12 13h.01" />
      </svg>
    </span>
  );
}
